import { Button, Modal, Tabs, useConfirm, useToast } from '@/components';
import { env } from '@/env';
import { useAuth } from '@/features/auth/AuthProvider';
import { JobsTable } from '@/features/runner-monitor/JobsTable';
import { MachineStats } from '@/features/runner-monitor/MachineCard';
import { TokenReveal } from '@/features/runner-monitor/MachineTokenModal';
import { ResourceCharts } from '@/features/runner-monitor/ResourceCharts';
import { QueryStates, RunnerPage } from '@/features/runner-monitor/RunnerPage';
import { RunnersTable } from '@/features/runner-monitor/RunnersTable';
import {
  Ago,
  MachineStatusBadge,
  MockBadge,
  StaleNotice,
} from '@/features/runner-monitor/StatusBadge';
import { dateTime } from '@/features/runner-monitor/format';
import {
  type SampleWindow,
  useDeleteMachine,
  useRotateMachineToken,
  useRunnerJobs,
  useRunnerMachine,
  useRunners,
} from '@/features/runner-monitor/hooks';
import { A, useNavigate, useParams, useSearchParams } from '@solidjs/router';
import { SAMPLE_RANGES, type SampleRange } from '@uchat-team-management/shared';
import { Show, createSignal } from 'solid-js';

export default function RunnersMachineDetail() {
  const params = useParams<{ machineId: string }>();
  const [search, setSearch] = useSearchParams<{ range?: string; from?: string; to?: string }>();
  const { session } = useAuth();
  const canManage = () => session()?.permissions.has('runners:machines:manage') ?? false;
  const machine = useRunnerMachine(() => params.machineId);
  const runners = useRunners();
  const jobs = useRunnerJobs(() => ({ machineId: params.machineId, limit: 10 }));
  const rotate = useRotateMachineToken(() => params.machineId);
  const remove = useDeleteMachine(() => params.machineId);
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  const [newToken, setNewToken] = createSignal<string | null>(null);

  const range = (): SampleRange =>
    (SAMPLE_RANGES as readonly string[]).includes(search.range ?? '')
      ? (search.range as SampleRange)
      : '1h';
  /** A job's own window (from History → Resources) wins over the preset ranges. */
  const window = (): SampleWindow =>
    search.from && search.to ? { from: search.from, to: search.to } : { range: range() };

  const onRotate = async () => {
    const ok = await confirm({
      title: 'Rotate the agent token?',
      body: 'The current token stops working immediately; the agent stays offline until its config has the new one.',
      confirmLabel: 'Rotate',
      danger: true,
    });
    if (!ok) return;
    const res = await rotate.mutateAsync();
    setNewToken(res.data.token);
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: `Remove ${machine.data?.data.name}?`,
      body: 'Its token and metric history are deleted. Runners stay listed from GitHub.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    await remove.mutateAsync();
    toast.success('Machine removed');
    navigate('/runners/machines');
  };

  return (
    <RunnerPage
      title={
        <span class="flex items-center gap-2">
          <A href="/runners/machines" class="text-neutral-400 hover:text-neutral-600">
            Machines /
          </A>
          {machine.data?.data.name ?? '…'}
          <Show when={machine.data?.data}>
            {(m) => (
              <>
                <MachineStatusBadge online={m().online} />
                <MockBadge source={m().source} />
              </>
            )}
          </Show>
        </span>
      }
      sub={
        <Show when={machine.data?.data}>
          {(m) => (
            <span>
              {m().os === 'macos' ? 'macOS' : 'Windows'} · {m().hostname ?? 'agent not seen yet'}
              {m().agentVersion ? ` · agent ${m().agentVersion}` : ''} · last seen{' '}
              <Ago iso={m().lastSeenAt} />
            </span>
          )}
        </Show>
      }
      updatedAt={machine.dataUpdatedAt}
      actions={
        <Show when={canManage() && machine.data}>
          <Button variant="secondary" onClick={onRotate} loading={rotate.isPending}>
            Rotate token
          </Button>
          <Button variant="danger" onClick={onDelete} loading={remove.isPending}>
            Remove
          </Button>
        </Show>
      }
    >
      <QueryStates pending={machine.isPending} error={machine.error} empty={false} emptyTitle="">
        <Show when={machine.data?.data}>
          {(m) => (
            <>
              <Show when={!m().online}>
                <StaleNotice lastSeenAt={m().lastSeenAt} />
              </Show>
              <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
                <h2 class="mb-3 text-sm font-semibold">Now</h2>
                <Show
                  when={m().latest}
                  fallback={
                    <p class="text-sm text-neutral-500">
                      No data yet — the agent has not reported.
                    </p>
                  }
                >
                  {(s) => (
                    <div classList={{ 'opacity-60': !m().online }}>
                      <MachineStats sample={s()} />
                    </div>
                  )}
                </Show>
              </div>

              <div class="flex flex-wrap items-center justify-between gap-3">
                <h2 class="text-sm font-semibold">History</h2>
                <Show
                  when={!(search.from && search.to)}
                  fallback={
                    <div class="flex items-center gap-3 text-sm">
                      <span class="text-neutral-500">
                        Job window {dateTime(search.from)} → {dateTime(search.to)}
                      </span>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setSearch({ from: undefined, to: undefined })}
                      >
                        Back to presets
                      </Button>
                    </div>
                  }
                >
                  <Tabs
                    tabs={[
                      { id: '1h', label: '1 hour' },
                      { id: '24h', label: '24 hours' },
                      { id: '7d', label: '7 days' },
                    ]}
                    active={range()}
                    onChange={(id) => setSearch({ range: id })}
                  />
                </Show>
              </div>
              <p class="text-xs text-neutral-500">
                Machine-level totals — when several runners share this machine these are not a
                per-job measurement. Per-runner CPU/RAM is in the runners table below.
              </p>
              <ResourceCharts machineId={m().id} window={window()} />

              <h2 class="text-sm font-semibold">Runners on this machine</h2>
              <RunnersTable
                runners={(runners.data?.data ?? []).filter((r) => r.machineId === m().id)}
              />

              <h2 class="text-sm font-semibold">Recent jobs</h2>
              <JobsTable jobs={jobs.data?.pages[0]?.data ?? []} loading={jobs.isPending} />
            </>
          )}
        </Show>
      </QueryStates>
      <Modal
        open={newToken() !== null}
        onClose={() => setNewToken(null)}
        title="New agent token"
        closeOnBackdrop={false}
      >
        <Show when={newToken()}>
          {(t) => (
            <div class="space-y-4">
              <TokenReveal
                token={t()}
                apiUrl={env.VITE_API_BASE_URL}
                os={machine.data?.data.os ?? 'macos'}
              />
              <div class="flex justify-end">
                <Button onClick={() => setNewToken(null)}>Done</Button>
              </div>
            </div>
          )}
        </Show>
      </Modal>
    </RunnerPage>
  );
}
